/**
 * AJAX Drawer Loader (Vanilla JS)
 * Loads bundle drawer and size guide content on-demand via Section Rendering API
 */
;(function () {
  const SECTION_ID = 'ajax-bundle-drawer'

  // Cache for already-fetched drawer content
  const drawerCache = new Map()
  // In-flight fetches: prevent duplicate requests for the same product
  const inFlightFetches = new Set()

  const selectors = {
    container: '[data-hook="ajax-drawer-container"]',
    trigger: '[data-ajax-drawer]',
    popup: '[data-component="popup"]',
    swiper: '[data-component="swiper"], [data-swiper]',
    clickToggle: '[data-utility="click-toggle"]',
    helpSource: '.bundle-builder-content-ajax-help',
    subscriptionBenefits: '.subscription-benefits-section',
    customerReviews: '[aria-labelledby="customer-reviews-title"]',
    cardVariantSelect: 'select[name="variant"], [data-hook="subscribe-variant"]',
    cardVariantHidden: '[data-hook="subscribe-variant-id"], input[name="variant"]',
    cardVariantPill: '[data-hook="subscribe-variant-pill"][aria-selected="true"]',
    card: '[data-hook="subscribe-card"], .bundler-product-card',
    cardQuantity: '[data-component="quantity-box"] [data-hook="quantity"]',
    drawerQuantity: '.quantity-wrapper [data-hook="quantity"], [data-component="quantity-box"] [data-hook="quantity"]',
    drawerVariantId: 'input[name="id"]',
    drawerProductOption: '[data-hook="product-option"]',
    drawerProductJson: 'script[data-hook="product-json"]',
  }

  const classes = window.config?.classes || {
    active: 'on',
    loading: 'loading',
  }

  function init() {
    // Intercept clicks on drawer triggers - using event delegation
    document.addEventListener('click', handleDrawerOpen)
    document.addEventListener('keydown', function (event) {
      if (event.key !== 'Enter' && event.key !== ' ') return
      const trigger = event.target.closest(selectors.trigger)
      if (!trigger || trigger.tagName === 'BUTTON' || trigger.tagName === 'A') return
      event.preventDefault()
      handleDrawerOpen(event)
    })
  }

  /**
   * Get the first available drawer container
   */
  function getContainer() {
    return document.querySelector(selectors.container)
  }

  /**
   * Merge subscription-benefits and customer-reviews from the page's
   * bundle-builder-content-ajax-help into the fetched drawer HTML.
   */
  function mergeHelpContent(html) {
    const helpSource = document.querySelector(selectors.helpSource)
    if (!helpSource) return html

    const doc = new DOMParser().parseFromString(html, 'text/html')
    const subBenefitSource = helpSource.querySelector(selectors.subscriptionBenefits)
    const reviewsSource = helpSource.querySelector(selectors.customerReviews)

    if (subBenefitSource) {
      const targetSub = doc.querySelector(selectors.subscriptionBenefits)
      if (targetSub) {
        // Replace entire section with full HTML from page's ajax-help
        targetSub.outerHTML = subBenefitSource.cloneNode(true).outerHTML
      }
    }
    if (reviewsSource) {
      const targetReviews = doc.querySelector(selectors.customerReviews)
      if (targetReviews) {
        // Replace entire section with full HTML from page's ajax-help
        targetReviews.outerHTML = reviewsSource.cloneNode(true).outerHTML
      }
    }

    return doc.body.innerHTML
  }

  function getSourceCard(trigger) {
    return trigger.closest(selectors.card)
  }

  function getCardVariantId(card) {
    if (!card) return ''

    const variantSelect = card.querySelector(selectors.cardVariantSelect)
    if (variantSelect?.value) return String(variantSelect.value)

    const hidden = card.querySelector(selectors.cardVariantHidden)
    if (hidden?.value) return String(hidden.value)

    const pill = card.querySelector(selectors.cardVariantPill)
    if (pill?.getAttribute('data-variant-id')) {
      return String(pill.getAttribute('data-variant-id'))
    }

    return ''
  }

  function getCardQuantity(card) {
    if (!card) return 0

    const fromData = parseInt(card.dataset.cartQuantity, 10)
    if (!Number.isNaN(fromData) && fromData >= 0) return fromData

    const input = card.querySelector(selectors.cardQuantity)
    if (input) return Math.max(0, parseInt(input.value, 10) || 0)

    return 0
  }

  /**
   * Sync drawer variant/options to match the current product selection on the page.
   * Reads the selected variant from the card (select, hidden input, or pill), then in the drawer
   * sets input[name="id"] and selects the matching [data-hook="product-option"] (radios/selects).
   */
  function syncDrawerVariant(trigger, container) {
    const card = getSourceCard(trigger)
    if (!card) return

    const currentVariantId = getCardVariantId(card)
    if (!currentVariantId) return

    const variantIdInput = container.querySelector(selectors.drawerVariantId)
    if (variantIdInput) {
      variantIdInput.value = currentVariantId
    }

    const jsonScript = container.querySelector(selectors.drawerProductJson)
    if (!jsonScript || !jsonScript.textContent) return

    let productData
    try {
      productData = JSON.parse(jsonScript.textContent.trim())
    } catch (e) {
      return
    }

    const variant = (productData.variants || []).find(function (v) {
      return String(v.id) === String(currentVariantId)
    })
    if (!variant) return

    const options = productData.options || []
    const optionValues = [variant.option1, variant.option2, variant.option3].filter(Boolean)

    container.querySelectorAll(selectors.drawerProductOption).forEach(function (el) {
      if (el.tagName === 'SELECT') {
        const optionName = el.getAttribute('name')
        const idx = options.indexOf(optionName)
        if (idx >= 0 && optionValues[idx] != null) {
          el.value = optionValues[idx]
          el.dispatchEvent(new Event('change', { bubbles: true }))
        }
      } else if (el.type === 'radio' && el.matches('input[data-hook="product-option"]')) {
        if (el.value === variant.option1) {
          el.checked = true
          el.dispatchEvent(new Event('change', { bubbles: true }))
        }
      }
    })
  }

  function makeQuantityReadonly(input) {
    if (!input) return
    input.readOnly = true
    input.setAttribute('aria-readonly', 'true')
    input.setAttribute('tabindex', '-1')
    input.classList.add('pointer-events-none')
  }

  /**
   * Sync drawer quantity box to match the product card / cart quantity.
   */
  function syncDrawerQuantity(trigger, container) {
    const card = getSourceCard(trigger)
    const qty = getCardQuantity(card)
    const drawerQtyInput = container.querySelector(selectors.drawerQuantity)

    if (drawerQtyInput) {
      makeQuantityReadonly(drawerQtyInput)
      if (String(drawerQtyInput.value) !== String(qty)) {
        drawerQtyInput.value = String(qty)
      }
    }

    const drawer = container.querySelector(selectors.popup)
    if (drawer) {
      const cartState = drawer.querySelector('[data-hook="drawer-cart-state"]')
      if (cartState) cartState.classList.toggle(classes.active, qty > 0)
    }
  }

  async function handleDrawerOpen(event) {
    const trigger = event.target.closest(selectors.trigger)
    if (!trigger) return

    event.preventDefault()
    event.stopPropagation()

    const productHandle = trigger.getAttribute('data-ajax-drawer')

    if (!productHandle) return

    const container = getContainer()
    if (!container) {
      console.warn('AJAX Drawer: No container found')
      return
    }

    // Prevent multiple simultaneous requests for the same product
    if (inFlightFetches.has(productHandle)) return

    // Show loading state on trigger
    trigger.classList.add(classes.loading)

    try {
      // Use cached HTML if we already loaded this product – no AJAX call again
      let html = drawerCache.get(productHandle)

      if (!html) {
        inFlightFetches.add(productHandle)
        // Fetch via Section Rendering API (only once per product)
        const url = `/products/${productHandle}?sections=${SECTION_ID}`
        const response = await fetch(url)

        if (!response.ok) {
          throw new Error(`Failed to fetch drawer: ${response.status}`)
        }

        const data = await response.json()
        html = data[SECTION_ID]

        // Merge subscription-benefits and customer-reviews from page's ajax-help
        html = mergeHelpContent(html)

        // Cache merged result so we never fetch or merge again for this product
        drawerCache.set(productHandle, html)
      }

      // Inject content into container
      container.innerHTML = html

      // Re-initialize components in the newly loaded content
      initDrawerComponents(container)

      // Sync drawer variant/options + quantity to current product selection / cart qty
      syncDrawerVariant(trigger, container)
      syncDrawerQuantity(trigger, container)

      // Find and open the drawer
      const drawer = container.querySelector(selectors.popup)
      if (drawer) {
        const event = new CustomEvent('overlay:show', {
          bubbles: true,
          detail: { lockScroll: true, yPosition: window.scrollY },
        })
        document.dispatchEvent(event)
        drawer.classList.add(classes.active)
        const overlay = document.querySelector('[data-component="global/overlay"]')
        if (overlay) overlay.style.zIndex = '61'

        // Trigger overlay - popup component handles scroll lock via toggleScroll
        // Dispatches CustomEvent for interop with existing jQuery listeners
        // Note: jQuery.on can listen to native CustomEvents if they bubble

        // Also trigger using jQuery if available for better compatibility with legacy code
        // if (window.jQuery) {
        //   window.jQuery(document).trigger('overlay:show');
        // }
      }
    } catch (error) {
      console.error('AJAX Drawer Error:', error)
    } finally {
      inFlightFetches.delete(productHandle)
      // Remove loading state
      trigger.classList.remove(classes.loading)
    }
  }

  /**
   * Re-initialize components in the newly loaded drawer content
   */
  function initDrawerComponents(container) {
    // Initialize Swiper instances
    const swipers = container.querySelectorAll(selectors.swiper)
    swipers.forEach((swiperEl) => {
      const swiperConfigStr = swiperEl.getAttribute('data-swiper')

      if (swiperConfigStr && window.Swiper) {
        try {
          const config = JSON.parse(swiperConfigStr)
          new window.Swiper(swiperEl, config)
        } catch (e) {
          console.warn('Failed to initialize Swiper in drawer:', e)
        }
      }
    })

    // Trigger component initialization for new content
    if (window.componentInit) {
      window.componentInit(container)
    }

    // Alternative: trigger a custom event
    const loadEvent = new CustomEvent('ajax:contentLoaded', {
      bubbles: true,
      detail: [container],
    })
    document.dispatchEvent(loadEvent)

    if (window.jQuery) {
      window.jQuery(document).trigger('ajax:contentLoaded', [window.jQuery(container)])
    }
  }

  /**
   * Close the drawer and clean up
   */
  function closeDrawer(targetSelector) {
    if (!targetSelector) return

    const target = document.querySelector(targetSelector)
    if (target) {
      target.classList.remove(classes.active)
    }

    const overlay = document.querySelector('[data-component="global/overlay"]')
    if (overlay) overlay.style.zIndex = ''

    // Hide overlay
    const event = new CustomEvent('overlay:hide', { bubbles: true })
    document.dispatchEvent(event)

    if (window.jQuery) {
      window.jQuery(document).trigger('overlay:hide')
    }
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }
})()
